import dayjs from 'dayjs'
import orderBy from 'lodash/orderBy'
import api from '@/api'
import { _surfaceErrorMessage, _surfaceSuccessMessage } from '@/snackbar'
import { ACTIVE_ENGAGEMENT_STATUSES, PLACED } from '../constants'
import { amountOfBusinessDays, FULL_DAY_IN_HOURS } from '@/helpers/dateHelper'
import { AMOUNT_OF_DAYS_TO_FILTER_INACTIVE_ENGAGEMENTS } from '../helpers'

export const getters = {
  filterEngagementsByClientId: (state) => (clientId) =>
    state.engagements.filter((engagement) => engagement.clientId === clientId),

  getSalesLeadUserNamesForEngagement: (state) => (engagement) => {
    if (!engagement.salesLeadUserIds) return []
    return engagement.salesLeadUserIds.map((id) => state.users[id]?.name)
  },

  getEngagementLeadUserNamesForEngagement: (state) => (engagement) => {
    if (!engagement.engagementLeadUserIds) return []
    return engagement.engagementLeadUserIds.map((id) => state.users[id]?.name)
  },

  getManagersForEngagement: (state) => (engagement) => {
    if (!engagement.managerIds) return []
    return engagement.managerIds.filter((id) => id in state.users).map((id) => state.users[id])
  },

  getEngagementsForClientView() {
    return (clientId, isShowingAllEngagements = false) => {
      const clientEngagements = isShowingAllEngagements
        ? this.filterEngagementsByClientId(clientId)
        : this.filterActiveEngagementsByClientId(clientId)
      const clientEngagementsWithSalesLeadUserNames = clientEngagements.map((engagement) => {
        const salesLeadUserNames = this.getSalesLeadUserNamesForEngagement(engagement)
        const engagementLeadUserNames = this.getEngagementLeadUserNamesForEngagement(engagement)
        const managerNames = this.getManagersForEngagement(engagement).map(({ name }) => name)
        return { ...engagement, salesLeadUserNames, engagementLeadUserNames, managerNames }
      })
      return orderBy(clientEngagementsWithSalesLeadUserNames, 'id', 'desc')
    }
  },

  canManageEngagement() {
    return (engagementId) =>
      (this.engagementsById[engagementId] || false) &&
      (this.isAdmin ||
        this.engagementsById[engagementId]?.engagementLeadUserIds.includes(this.currentUserId) ||
        this.engagementsById[engagementId]?.managerIds.includes(this.currentUserId))
  },

  filterActiveEngagementsByClientId() {
    return (clientId) =>
      this.filterEngagementsByClientId(clientId)
        .filter(
          (e) =>
            ACTIVE_ENGAGEMENT_STATUSES.includes(e.status) ||
            dayjs(e.statusUpdatedAt).isAfter(
              dayjs().subtract(AMOUNT_OF_DAYS_TO_FILTER_INACTIVE_ENGAGEMENTS, 'day')
            )
        )
        .filter((e) => this.isEngagementInProgress(e.id))
  },

  isEngagementInProgress() {
    return (engagementId) => {
      const staffingRoles = this.getStaffingRolesByEngagementId(engagementId)
      const placements = staffingRoles.map((staffingRole) =>
        this.getPlacementsForStaffingRole(staffingRole.id)
      )
      return (
        staffingRoles.length === 0 ||
        placements.some((placement) => placement.length === 0) ||
        staffingRoles.some((staffingRole) => dayjs().isBefore(dayjs(staffingRole.endDate))) ||
        placements.flat().some((placement) => placement.status !== PLACED)
      )
    }
  },

  getStaffingEstimatedRevenueAmount() {
    return (engagementId) => {
      const staffingRoles = this.getStaffingRolesByEngagementId(engagementId)
      if (staffingRoles.length === 0) return 0
      return staffingRoles.reduce(
        (acc, sr) =>
          acc +
          sr.rate *
            ((sr.percentageUtilization / 100) * FULL_DAY_IN_HOURS) *
            amountOfBusinessDays(dayjs(sr.startDate), dayjs(sr.endDate)),
        0
      )
    }
  },
}

export const actions = {
  async fetchEngagementsById() {
    this.engagementsById = await api.fetchEngagementsById()
  },

  async saveEngagement(data) {
    try {
      const newEngagement = await api.saveEngagement(data)
      this.engagements.push(newEngagement)
      this.engagementsById = { ...this.engagementsById, [newEngagement.id]: newEngagement }
      _surfaceSuccessMessage('Added!')
    } catch {
      _surfaceErrorMessage('Creation failed! Please try again.')
    }
  },

  async deleteEngagement(engagement) {
    try {
      await api.deleteEngagement(engagement)
      this.engagements = this.engagements.filter((e) => e.id !== engagement.id)
      const { [engagement.id]: _deleted, ...rest } = this.engagementsById
      this.engagementsById = rest
      _surfaceSuccessMessage('Successfully deleted!')
    } catch {
      _surfaceErrorMessage('Could not be deleted!')
    }
  },

  async updateEngagement(engagementParams) {
    try {
      this.engagements = this.engagements.map((e) =>
        e.id === engagementParams.id ? { ...e, updateInFlight: true } : e
      )
      this.engagementsById = {
        ...this.engagementsById,
        [engagementParams.id]: {
          ...this.engagementsById[engagementParams.id],
          updateInFlight: true,
        },
      }
      const updatedEngagement = await api.updateEngagement(engagementParams)
      this.engagements = this.engagements.map((e) =>
        e.id === updatedEngagement.id
          ? { ...e, ...updatedEngagement, inEditState: false, updateInFlight: false }
          : e
      )
      this.engagementsById = {
        ...this.engagementsById,
        [updatedEngagement.id]: {
          ...this.engagementsById[updatedEngagement.id],
          ...updatedEngagement,
          inEditState: false,
          updateInFlight: false,
        },
      }
      _surfaceSuccessMessage('Updated!')
    } catch {
      _surfaceErrorMessage('Update failed! Please try again.')
      this.engagements = this.engagements.map((e) =>
        e.id === engagementParams.id ? { ...e, updateInFlight: false } : e
      )
      this.engagementsById = {
        ...this.engagementsById,
        [engagementParams.id]: {
          ...this.engagementsById[engagementParams.id],
          updateInFlight: false,
        },
      }
    }
  },
}
